import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from '@/components/icons';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { TestDataWithCollections } from '@/components/testdata/TestDataCollections';
import { EmptyState, ErrorNotice, PageHeader, PageLoader } from '@/components/ui';

/** Organization-wide test data (/test-data for the current org, or /orgs/:orgId/test-data). */
export function OrgTestDataPage() {
  const { orgId: routeOrgId } = useParams();
  const { currentOrgId, memberships, can } = useAuth();
  const orgId = routeOrgId ?? currentOrgId;

  const vars = useQuery({ queryKey: ['org-variables', orgId], queryFn: () => api.orgVariables(orgId!), enabled: !!orgId });

  if (!orgId) {
    return <EmptyState icon={<Building2 className="size-6" />} title="No organization selected" description="Join or create an organization first." />;
  }
  if (vars.isPending) return <PageLoader />;
  if (vars.isError) return <ErrorNotice error={vars.error} onRetry={() => vars.refetch()} />;

  const orgName = memberships.find((m) => m.orgId === orgId)?.orgName;
  const canEdit = can('variables.org.manage', orgId);
  const isAdmin = can('test.manage.any', orgId);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={orgName ?? 'Organization'}
        title="Test data"
        description="Test data for every project in this organization - for example a test user's email and password. Shared values apply in every environment; collections (Dev, Staging, Preprod...) hold what differs per environment. A project can override a value with its own."
      />
      <TestDataWithCollections
        scope="ORG"
        orgId={orgId}
        variables={vars.data}
        canEdit={canEdit}
        canCreateCollections={canEdit}
        createVariable={(input) => api.createOrgVariable(orgId, input)}
        variableQueryKeys={[['org-variables', orgId], ['project-variables']]}
        sharedDescription={
          !canEdit
            ? 'Only Project Admins and Project Members can change organization test data.'
            : isAdmin
              ? 'Available in all projects of this organization.'
              : 'Available in all projects of this organization. You can change the values you added; copy anyone else’s to use your own.'
        }
      />
    </div>
  );
}
